import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DateInput from './DateInput';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ClientSideRowModelModule, type ColDef, ModuleRegistry } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { useGridCopyMenu } from './gridCommon';
import { callAxiosGet } from '../tmslib/src/util/axiosutils';
import { useMessageState } from '../tmslib/src/context/MessageContext';
import { getMainAxios } from '../utils/util';
import StockChart from './StockChart';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

interface EtfDailyRow {
  d: string;
  prodId: string;
  prodNm: string;
  ltd?: string | null;   // 상장일 (kospimst.ltd)
  // OHLCV (kwproddaily)
  o?: number | null;
  h?: number | null;
  l?: number | null;
  c?: number | null;
  v?: number | null;
  ta?: number | null;
  // ETF 헤더
  stckPrpr?: number | null;
  prdyVrss?: number | null;
  prdyCtrt?: number | null;
  nav?: number | null;
  navPrdyVrss?: number | null;
  navPrdyCtrt?: number | null;
  etfNtasTtam?: number | null;
  etfCnfgIssuAvls?: number | null;
  etfCuUnitScrtCnt?: number | null;
  etfCnfgIssuCnt?: number | null;
  pdfCnt: number;
}

interface EtfPdfRow {
  prodPdf: string;
  prodNm: string;
  // OHLCV (kwproddaily)
  o?: number | null;
  h?: number | null;
  l?: number | null;
  c?: number | null;
  v?: number | null;
  ta?: number | null;
  // ETF PDF 정보
  wei?: number | null;
  rt?: number | null;
  tdyRt?: number | null;
  valAmt?: number | null;
}

// ── 운용사 판별 ──────────────────────────────────────────────
// ETF 종목명은 "브랜드 + 기초지수" 형태라 첫 토큰이 곧 운용사 브랜드다.
// (실측 2026-08-28: TIGER 123 / KODEX 122 / ACE 50 / TIME 5)
// 브랜드→운용사 매핑은 알려진 것만 두고, 나머지는 브랜드명을 그대로 쓴다.
const BRAND_TO_COMPANY: Record<string, string> = {
  KODEX: '삼성자산운용',
  TIGER: '미래에셋자산운용',
  ACE: '한국투자신탁운용',
  KBSTAR: 'KB자산운용',
  RISE: 'KB자산운용',
  SOL: '신한자산운용',
  PLUS: '한화자산운용',
  ARIRANG: '한화자산운용',
  HANARO: 'NH아문디자산운용',
  TIMEFOLIO: '타임폴리오자산운용',
  TIME: '타임폴리오자산운용',
  WOORI: '우리자산운용',
  BNK: 'BNK자산운용',
  히어로즈: '키움투자자산운용',
  마이다스: '마이다스에셋자산운용',
};

const brandOf = (prodNm?: string | null): string => {
  const nm = (prodNm ?? '').trim();
  if (!nm) return '(미분류)';
  return nm.split(/\s+/)[0];
};

const companyOf = (prodNm?: string | null): string => {
  const b = brandOf(prodNm);
  return BRAND_TO_COMPANY[b] ?? b;
};

// 필터 기본값 — 화면을 열면 바로 이 조건으로 보인다.
const DEFAULT_MIN_NAV = 1000;          // 순자산 하한(억). 소형 ETF 를 걸러낸다
const DEFAULT_BRAND = 'KODEX';         // 운용사(브랜드)
const DEFAULT_SIDE: 'all' | 'up' | 'down' = 'up';   // 등락

const numFmt = (v: any) => v == null ? '' : Number(v).toLocaleString();
const pctFmt = (v: any) => v == null ? '' : `${Number(v).toFixed(2)}`;
const signColor = (v: any) => {
  const n = Number(v);
  return n > 0 ? '#dc2626' : n < 0 ? '#1d4ed8' : '#374151';
};

const EtfAnalysis: React.FC = () => {
  const copyMenu1 = useGridCopyMenu();
  const copyMenu2 = useGridCopyMenu();
  // 최초 마운트 시 서버에서 최근 일자 자동 사용 (date 비워두면 백엔드가 max d 반환)
  const [date, setDate] = useState('');
  // 최소 수신건수 UI 는 제거했다. 서버 파라미터는 0(전체)로 고정한다.
  const minPdfCnt = 0;
  const [dailyRows, setDailyRows] = useState<EtfDailyRow[]>([]);
  const [pdfRows, setPdfRows] = useState<EtfPdfRow[]>([]);
  const [selected, setSelected] = useState<EtfDailyRow | null>(null);
  const [selectedPdf, setSelectedPdf] = useState<EtfPdfRow | null>(null);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);

  // ── 클라이언트 필터 (서버 재조회 없이 즉시 반영) ──
  const [brandFilter, setBrandFilter] = useState<string>(DEFAULT_BRAND);      // 운용사(브랜드)
  const [nameFilter, setNameFilter] = useState<string>('');     // 종목명/코드 검색
  const [minNav, setMinNav] = useState<number>(DEFAULT_MIN_NAV); // 순자산총액 하한(억)
  const [sideFilter, setSideFilter] = useState<'all' | 'up' | 'down'>(DEFAULT_SIDE); // 등락

  const { msgBox: m, logger } = useMessageState();
  const axiosInstance = getMainAxios();

  const fetchDaily = useCallback((d?: string) => {
    setLoadingDaily(true);
    const params: Record<string, any> = { minPdfCnt };
    const useDate = d ?? date;
    if (useDate) params.d = useDate;
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/etf/daily',
      params,
      onSuccess: (data) => {
        const rows = (data as EtfDailyRow[]) || [];
        setDailyRows(rows);
        // 응답의 d 를 입력 박스에도 반영 (최근일자 자동)
        if (!useDate && rows.length > 0) setDate(rows[0].d);
        setLoadingDaily(false);
      },
      onError: () => { setDailyRows([]); setLoadingDaily(false); },
    });
  }, [axiosInstance, m, logger, date, minPdfCnt]);

  const fetchPdf = useCallback((row: EtfDailyRow) => {
    setSelected(row);
    setSelectedPdf(null);
    setLoadingPdf(true);
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/etf/portfolio',
      params: { prodId: row.prodId, d: row.d },
      onSuccess: (data) => { setPdfRows((data as EtfPdfRow[]) || []); setLoadingPdf(false); },
      onError: () => { setPdfRows([]); setLoadingPdf(false); },
    });
  }, [axiosInstance, m, logger]);

  useEffect(() => { fetchDaily(); }, []);

  // 브랜드 목록 — 건수 많은 순
  const brandOptions = useMemo(() => {
    const c = new Map<string, number>();
    dailyRows.forEach(r => { const b = brandOf(r.prodNm); c.set(b, (c.get(b) ?? 0) + 1); });
    return Array.from(c.entries()).sort((a, b) => b[1] - a[1]);
  }, [dailyRows]);

  // 필터 적용 결과 — 그리드와 요약이 같은 데이터를 본다
  const filteredRows = useMemo(() => {
    const kw = nameFilter.trim().toLowerCase();
    return dailyRows.filter(r => {
      if (brandFilter && brandOf(r.prodNm) !== brandFilter) return false;
      if (kw && !(`${r.prodNm ?? ''} ${r.prodId}`.toLowerCase().includes(kw))) return false;
      // etfNtasTtam(순자산총액)은 KIS 가 이미 억 단위로 준다(실측: 624 = 624억).
      if (minNav > 0 && (r.etfNtasTtam ?? 0) < minNav) return false;
      const ctrt = Number(r.prdyCtrt ?? 0);
      if (sideFilter === 'up' && !(ctrt > 0)) return false;
      if (sideFilter === 'down' && !(ctrt < 0)) return false;
      return true;
    });
  }, [dailyRows, brandFilter, nameFilter, minNav, sideFilter]);

  const dailyCols = useMemo<ColDef<EtfDailyRow>[]>(() => [
    { field: 'prodId', headerName: '종목코드', width: 100, pinned: 'left', cellStyle: { fontFamily: 'monospace', fontWeight: 500 } },
    { field: 'prodNm', headerName: '종목명', width: 230, pinned: 'left', filter: 'agTextColumnFilter' },
    // OHLCV (kwproddaily)
    { field: 'o', headerName: '시가', width: 90, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
    { field: 'h', headerName: '고가', width: 90, type: 'numericColumn', valueFormatter: p => numFmt(p.value), cellStyle: { color: '#dc2626' } },
    { field: 'l', headerName: '저가', width: 90, type: 'numericColumn', valueFormatter: p => numFmt(p.value), cellStyle: { color: '#1d4ed8' } },
    { field: 'c', headerName: '종가', width: 90, type: 'numericColumn', valueFormatter: p => numFmt(p.value), cellStyle: { fontWeight: 600 } },
    { field: 'v', headerName: '거래량', width: 110, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
    { field: 'ta', headerName: '거래대금', width: 120, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
    // 운용사 — 종목명 첫 토큰(브랜드)에서 유도. 원천 컬럼이 없어 계산 컬럼으로 둔다.
    { colId: 'brand', headerName: '운용사', width: 130,
      valueGetter: (p: any) => p.data ? companyOf(p.data.prodNm) : '',
      cellStyle: { fontSize: '11px', color: '#475569' } },
    { field: 'ltd', headerName: '상장일', width: 105,
      valueFormatter: p => p.value ? String(p.value).slice(0, 10) : '',
      cellStyle: { fontSize: '11px', color: '#475569' } },
    // ETF 헤더
    { field: 'stckPrpr', headerName: '현재가', width: 100, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
    { field: 'prdyVrss', headerName: '전일대비', width: 100, type: 'numericColumn', valueFormatter: p => numFmt(p.value), cellStyle: (p: any) => ({ textAlign: 'right', color: signColor(p.value) }) },
    { field: 'prdyCtrt', headerName: '등락률(%)', width: 100, type: 'numericColumn', sort: 'desc', valueFormatter: p => pctFmt(p.value), cellStyle: (p: any) => ({ textAlign: 'right', color: signColor(p.value) }) },
    { field: 'nav', headerName: 'NAV', width: 110, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
    { field: 'navPrdyVrss', headerName: 'NAV대비', width: 100, type: 'numericColumn', valueFormatter: p => numFmt(p.value), cellStyle: (p: any) => ({ textAlign: 'right', color: signColor(p.value) }) },
    { field: 'navPrdyCtrt', headerName: 'NAV등락률(%)', width: 110, type: 'numericColumn', valueFormatter: p => pctFmt(p.value), cellStyle: (p: any) => ({ textAlign: 'right', color: signColor(p.value) }) },
    { field: 'etfNtasTtam', headerName: '순자산총액(억)', width: 130, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
    { field: 'etfCnfgIssuAvls', headerName: '구성시총', width: 120, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
    { field: 'etfCuUnitScrtCnt', headerName: 'CU증권수', width: 110, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
    { field: 'etfCnfgIssuCnt', headerName: '구성종목수', width: 100, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
    { field: 'pdfCnt', headerName: '수신건수', width: 90, type: 'numericColumn', cellStyle: (p: any) => ({ textAlign: 'right', color: p.value === 0 ? '#9ca3af' : '#0f172a', fontWeight: 500 }) },
  ], []);

  const pdfCols = useMemo<ColDef<EtfPdfRow>[]>(() => [
    { field: 'prodPdf', headerName: '종목코드', width: 100, cellStyle: { fontFamily: 'monospace', fontWeight: 500 } },
    { field: 'prodNm', headerName: '종목명', width: 220, filter: 'agTextColumnFilter' },
    { field: 'wei', headerName: '비중(%)', width: 100, type: 'numericColumn', valueFormatter: p => pctFmt(p.value), cellStyle: { textAlign: 'right', fontWeight: 600 } },
    // OHLCV (kwproddaily)
    { field: 'o', headerName: '시가', width: 90, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
    { field: 'h', headerName: '고가', width: 90, type: 'numericColumn', valueFormatter: p => numFmt(p.value), cellStyle: { color: '#dc2626' } },
    { field: 'l', headerName: '저가', width: 90, type: 'numericColumn', valueFormatter: p => numFmt(p.value), cellStyle: { color: '#1d4ed8' } },
    { field: 'c', headerName: '종가', width: 90, type: 'numericColumn', valueFormatter: p => numFmt(p.value), cellStyle: { fontWeight: 600 } },
    { field: 'v', headerName: '거래량', width: 120, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
    { field: 'ta', headerName: '거래대금', width: 130, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
    { field: 'rt', headerName: '등락률(%)', width: 100, type: 'numericColumn', valueFormatter: p => pctFmt(p.value), cellStyle: (p: any) => ({ textAlign: 'right', color: signColor(p.value) }) },
    { field: 'tdyRt', headerName: '당일등락률(%)', width: 100, type: 'numericColumn', valueFormatter: p => pctFmt(p.value), cellStyle: (p: any) => ({ textAlign: 'right', color: signColor(p.value) }) },
    { field: 'valAmt', headerName: '평가금액', width: 130, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
  ], []);

  const labelStyle: React.CSSProperties = { fontWeight: 'bold', color: '#1e40af', fontSize: '12px', whiteSpace: 'nowrap' };
  const inputStyle: React.CSSProperties = { padding: '6px 10px', border: '2px solid #3b82f6', borderRadius: '4px', fontSize: '12px', outline: 'none' };
  const btnStyle: React.CSSProperties = { padding: '5px 12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 };

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {/* 헤더 — 조회조건 + 필터를 한 줄에 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
                    padding: '10px 14px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap', overflowX: 'auto' }}>
          <label style={labelStyle}>일자</label>
          <DateInput value={date} onChange={setDate} style={{ border: '2px solid #3b82f6' }} />

          <button onClick={() => fetchDaily()} style={btnStyle}>{loadingDaily ? '조회중' : '조회'}</button>

          <span style={{ width: '1px', height: '20px', backgroundColor: '#e2e8f0', flexShrink: 0 }} />

          <label style={labelStyle}>운용사</label>
          <select value={brandFilter} onChange={e => setBrandFilter(e.target.value)}
                  style={{ ...inputStyle, minWidth: '140px', flexShrink: 0 }}>
            <option value="">전체 ({dailyRows.length})</option>
            {brandOptions.map(([b, cnt]) => (
              <option key={b} value={b}>
                {b}{BRAND_TO_COMPANY[b] ? ` · ${BRAND_TO_COMPANY[b]}` : ''} ({cnt})
              </option>
            ))}
          </select>

          <label style={labelStyle}>검색</label>
          <input type="text" placeholder="종목명/코드" value={nameFilter}
                 onChange={e => setNameFilter(e.target.value)}
                 style={{ ...inputStyle, width: '130px', flexShrink: 0 }} />

          <label style={labelStyle}>순자산 ≥</label>
          <input type="number" min={0} step={100} value={minNav}
                 onChange={e => setMinNav(parseInt(e.target.value) || 0)}
                 style={{ ...inputStyle, width: '80px', textAlign: 'right', flexShrink: 0 }} />
          <span style={{ fontSize: '11px', color: '#64748b', flexShrink: 0 }}>억</span>

          <label style={labelStyle}>등락률</label>
          {([['all', '전체'], ['up', '상승'], ['down', '하락']] as const).map(([v, lbl]) => (
            <button key={v} onClick={() => setSideFilter(v)}
              style={{
                padding: '4px 10px', fontSize: '11px', borderRadius: '4px', cursor: 'pointer', flexShrink: 0,
                border: sideFilter === v ? '1px solid #3b82f6' : '1px solid #cbd5e1',
                backgroundColor: sideFilter === v ? '#dbeafe' : 'white',
                color: sideFilter === v ? '#1e40af' : '#475569',
                fontWeight: sideFilter === v ? 700 : 400,
              }}>{lbl}</button>
          ))}

          <button onClick={() => { setBrandFilter(DEFAULT_BRAND); setNameFilter(''); setMinNav(DEFAULT_MIN_NAV); setSideFilter(DEFAULT_SIDE); }}
            style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid #cbd5e1', flexShrink: 0,
                     borderRadius: '4px', backgroundColor: 'white', color: '#64748b', cursor: 'pointer' }}>
            초기화
          </button>

          <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#1e40af',
                         fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {filteredRows.length.toLocaleString()}
            {filteredRows.length !== dailyRows.length && (
              <span style={{ color: '#64748b' }}> / {dailyRows.length.toLocaleString()}</span>
            )}건
          </span>
        </div>
      </div>

      {/* 위 그리드 — kisetfdaily */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#1e40af', fontWeight: 700 }}>
          ETF 일별 헤더 (kisetfdaily)
        </h3>
        <div className="ag-theme-quartz" style={{ height: 350, width: '100%', fontSize: '11px' }}>
          <AgGridReact {...copyMenu1.gridProps}
            rowData={filteredRows}
            columnDefs={dailyCols}
            defaultColDef={{ resizable: true, sortable: true, cellStyle: { fontSize: '11px' } }}
            rowSelection="single"
            onRowClicked={e => { if (e.data) fetchPdf(e.data); }}
            getRowId={p => p.data.prodId}
          />
        </div>
      </div>

      {/* 중간 그리드 — kisetfpdf 구성종목 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '12px', color: '#1e40af', fontWeight: 700 }}>
            구성종목 (kisetfpdf)
            {selected && (
              <span style={{ marginLeft: '12px', fontSize: '12px', fontWeight: 500, color: '#0f172a' }}>
                — {selected.prodNm} <span style={{ color: '#64748b', fontFamily: 'monospace' }}>{selected.prodId}</span>
                <span style={{ marginLeft: '12px', color: '#64748b' }}>
                  명목 {numFmt(selected.etfCnfgIssuCnt)}건 / 수신 {selected.pdfCnt}건
                </span>
              </span>
            )}
          </h3>
          {loadingPdf && <span style={{ fontSize: '12px', color: '#1e40af' }}>로딩중...</span>}
        </div>
        <div className="ag-theme-quartz" style={{ height: 280, width: '100%', fontSize: '11px' }}>
          <AgGridReact {...copyMenu2.gridProps}
            rowData={pdfRows}
            columnDefs={pdfCols}
            defaultColDef={{ resizable: true, sortable: true, cellStyle: { fontSize: '11px' } }}
            rowSelection="single"
            onRowClicked={e => { if (e.data) setSelectedPdf(e.data); }}
            getRowId={p => p.data.prodPdf}
          />
        </div>
      </div>

      {/* 하단 차트 — 선택한 구성종목 또는 직접 입력 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', color: '#1e40af', fontWeight: 700 }}>종목 차트</span>
          {selectedPdf && (
            <span style={{ fontSize: '12px', fontWeight: 500, color: '#0f172a' }}>
              — {selectedPdf.prodNm} <span style={{ color: '#64748b', fontFamily: 'monospace' }}>{selectedPdf.prodPdf}</span>
            </span>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#1e40af' }}>종목코드:</span>
          <input
            type="text" placeholder="예: 005930" value={selectedPdf?.prodPdf ?? ''}
            onChange={e => {
              const v = e.target.value.trim();
              if (v) setSelectedPdf({ prodPdf: v, prodNm: '' });
              else setSelectedPdf(null);
            }}
            style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px', width: '110px', fontFamily: 'monospace' }}
          />
        </div>
        {selectedPdf && (
          <StockChart prodId={selectedPdf.prodPdf} prodNm={selectedPdf.prodNm} height={400} />
        )}
      </div>
      {copyMenu1.menu}
      {copyMenu2.menu}
    </div>
  );
};

export default EtfAnalysis;
